import axios from 'axios';
import { storage, resetStorages } from 'utils/appLocalStorage';

let refreshPromise = null;
let onSessionInvalid = null;
let lastTokensPersistedAt = 0;

const REFRESH_LOCK_NAME = 'agroger-session-refresh';
const REFRESH_LOCK_STORAGE_KEY = `${storage.refreshToken}:lock`;
const LOCK_TTL_MS = 30000;
const RECOVERY_WAIT_MS = 2000;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function createTabId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_) {
    /* ignore */
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const tabId = createTabId();

export function setOnSessionInvalid(callback) {
  onSessionInvalid = callback;
}

export function getAccessToken() {
  return (
    localStorage.getItem(storage.accessToken) ||
    localStorage.getItem(storage.token)
  );
}

export function getRefreshToken() {
  return localStorage.getItem(storage.refreshToken);
}

export function persistSessionTokens({ accessToken, refreshToken }) {
  if (accessToken) {
    localStorage.setItem(storage.accessToken, accessToken);
    localStorage.removeItem(storage.token);
  }
  if (refreshToken) {
    localStorage.setItem(storage.refreshToken, refreshToken);
  }
  if (accessToken || refreshToken) {
    lastTokensPersistedAt = Date.now();
  }
}

/** Sessão acabou de ser gravada (login/refresh) — falhas antigas não devem deslogar. */
export function wasSessionRecentlyPersisted(withinMs = 8000) {
  if (!lastTokensPersistedAt) {
    return false;
  }
  return Date.now() - lastTokensPersistedAt <= withinMs;
}

export function clearSessionTokens() {
  localStorage.removeItem(storage.accessToken);
  localStorage.removeItem(storage.refreshToken);
  localStorage.removeItem(storage.token);
}

export function isAuthRefreshUrl(url = '', method = 'get') {
  const path = String(url).split('?')[0];
  const normalizedMethod = String(method).toLowerCase();

  if (path.includes('/session/refresh') || path.includes('/session/logout')) {
    return true;
  }

  return normalizedMethod === 'post' && /\/session\/?$/.test(path);
}

export function classifyRefreshFailure(error) {
  if (!error) {
    return 'unauthorized';
  }

  if (error.refreshKind) {
    return error.refreshKind;
  }

  const status = error.response?.status;
  const type = error.response?.data?.type;

  if (type === 'financeiro') {
    return 'financial';
  }

  if (
    !error.response ||
    error.code === 'ECONNABORTED' ||
    error.code === 'ERR_NETWORK'
  ) {
    return 'network';
  }

  if (status === 401) {
    return 'unauthorized';
  }

  if (status >= 500) {
    return 'server';
  }

  if (status >= 400) {
    return 'unauthorized';
  }

  return 'network';
}

export function notifySessionInvalid(payload) {
  if (typeof onSessionInvalid === 'function') {
    onSessionInvalid(payload);
    return;
  }
  resetStorages();
}

function tokensIfRotated(previousRefreshToken) {
  const currentRefresh = getRefreshToken();
  const currentAccess = getAccessToken();

  if (
    previousRefreshToken &&
    currentRefresh &&
    currentRefresh !== previousRefreshToken &&
    currentAccess
  ) {
    return {
      accessToken: currentAccess,
      refreshToken: currentRefresh,
    };
  }

  return null;
}

function waitForRotatedTokens(previousRefreshToken, timeoutMs = RECOVERY_WAIT_MS) {
  const already = tokensIfRotated(previousRefreshToken);
  if (already) {
    return Promise.resolve(already);
  }

  return new Promise((resolve) => {
    const started = Date.now();
    let settled = false;
    let intervalId = null;
    let check = null;

    function onStorage(event) {
      if (
        event.key === storage.refreshToken ||
        event.key === storage.accessToken
      ) {
        if (check) check();
      }
    }

    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('storage', onStorage);
      if (intervalId) {
        clearInterval(intervalId);
      }
      resolve(value);
    };

    check = () => {
      const rotated = tokensIfRotated(previousRefreshToken);
      if (rotated) {
        finish(rotated);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        finish(null);
      }
    };

    window.addEventListener('storage', onStorage);
    intervalId = setInterval(check, 100);
    check();
  });
}

function readLock() {
  try {
    return JSON.parse(localStorage.getItem(REFRESH_LOCK_STORAGE_KEY) || 'null');
  } catch (_) {
    return null;
  }
}

function clearOwnLock() {
  try {
    const current = readLock();
    if (current?.owner === tabId) {
      localStorage.removeItem(REFRESH_LOCK_STORAGE_KEY);
    }
  } catch (_) {
    localStorage.removeItem(REFRESH_LOCK_STORAGE_KEY);
  }
}

async function tryAcquireLocalStorageLock(fn) {
  const now = Date.now();
  const lock = readLock();
  const lockExpired = !lock?.expiresAt || lock.expiresAt < now;

  if (!lockExpired && lock.owner !== tabId) {
    return { ok: false };
  }

  localStorage.setItem(
    REFRESH_LOCK_STORAGE_KEY,
    JSON.stringify({ owner: tabId, expiresAt: now + LOCK_TTL_MS }),
  );

  const verified = readLock();
  if (verified?.owner !== tabId) {
    return { ok: false };
  }

  try {
    const value = await fn();
    return { ok: true, value };
  } finally {
    clearOwnLock();
  }
}

async function withLocalStorageLock(fn, startedAt = Date.now()) {
  const acquired = await tryAcquireLocalStorageLock(fn);
  if (acquired.ok) {
    return acquired.value;
  }

  if (Date.now() - startedAt >= LOCK_TTL_MS) {
    return fn();
  }

  await sleep(50);
  return withLocalStorageLock(fn, startedAt);
}

async function withRefreshLock(fn) {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, fn);
  }
  return withLocalStorageLock(fn);
}

async function performRefreshRequest(apiUrl, refreshToken) {
  const { data } = await axios.post(
    `${apiUrl.replace(/\/$/, '')}/session/refresh`,
    { refreshToken },
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    },
  );

  persistSessionTokens({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  });

  return data;
}

async function requestRefresh(apiUrl) {
  return withRefreshLock(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      const error = new Error('Refresh token ausente');
      error.refreshKind = 'unauthorized';
      throw error;
    }

    try {
      return await performRefreshRequest(apiUrl, refreshToken);
    } catch (error) {
      const kind = classifyRefreshFailure(error);

      if (kind === 'unauthorized') {
        const recovered =
          tokensIfRotated(refreshToken) ||
          (await waitForRotatedTokens(refreshToken));

        if (recovered) {
          return recovered;
        }
      }

      error.refreshKind = kind;
      throw error;
    }
  });
}

export function refreshSession(apiUrl) {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = requestRefresh(apiUrl).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}
